import { faBuilding } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Checkbox,
  DialogContent,
  FormControl,
  FormControlLabel,
  Grid,
  TextField,
  Typography
} from '@mui/material';
import { FormAddress, FormLine, SelectLabel } from 'components';
import FormCountryIdentifier from 'components/Form/FormCountryIdentifier/FormCountryIdentifier';
import { ModalHeader } from 'components/Modals/ModalHeader';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { CompanyService } from 'services';
import { DISTRIBUTOR_ROLES } from 'utils/constants';
import { translate, UserHelper } from 'utils/helpers';
import { CustomActionButtons } from './_CustomActionButtons';

export const CreateInstitutionModal = ({
                                         onClose,
                                         onConfirm,
                                         closeOnSubmit,
                                         defaultValues,
                                         parentCompanyId,
                                         parentInstitutionGroupId
                                       }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [identifierFields, setIdentifierFields] = useState([]);
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    clearErrors,
    formState: { errors }
  } = useForm({
    defaultValues: {
      ...defaultValues,
      fileNumberPrefix: defaultValues?.fileNumberPrefix ?? '',
      activated: defaultValues?.activated !== null ? defaultValues?.activated : true,
      attestationNumber: defaultValues?.attestationNumber ?? '',
      address1: (defaultValues?.address?.address1) ?? '',
      address2: (defaultValues?.address?.address2) ?? '',
      postalCode: (defaultValues?.address?.postalCode) ?? '',
      city: (defaultValues?.address?.city) ?? '',
      country: (defaultValues?.address?.country) ?? '',
      automaticCerfaDelivery: (defaultValues?.config?.automaticCerfaDelivery) ?? false,
      institutionName: defaultValues?.name ?? ''
    }
  });

  useEffect(() => {
    if (defaultValues?.id) {
      setValue('isPublic', defaultValues.isPublic);
    } else if (parentCompanyId) {
      CompanyService.isPublic(parentCompanyId)
          .then((isPublic) => setValue('isPublic', isPublic));
    }
  }, [parentCompanyId, defaultValues, setValue]);
  const onSubmit = useCallback((data) => {
    setIsLoading(true);

    const newInstitution = {
      ...data,
      identifiers: identifierFields,
      id: defaultValues?.id || null,
      name: data.institutionName || '',
      config: {
        automaticCerfaDelivery: data.automaticCerfaDelivery ?? false
      },
      address: {
        hashId: (defaultValues?.address?.hashId) || null,
        address1: data.address1,
        address2: data.address2,
        postalCode: data.postalCode,
        city: data.city,
        country: {
          ...data.country,
          countryCode: data.country?.countryCode
        }
      },
      parentInstitutionGroupId,
      parentCompanyId
    };

    onConfirm(newInstitution)
        .finally(() => setIsLoading(false));

    if (closeOnSubmit) {
      onClose();
    }
  }, [identifierFields, defaultValues, parentInstitutionGroupId, parentCompanyId, onConfirm, closeOnSubmit, onClose]);

  return (
      <form
          autoComplete="off"
          name="institutionForm"
          style={{ overflow: 'auto' }}
          onSubmit={handleSubmit(onSubmit)}
      >
        <ModalHeader onClose={onClose}>
          <FontAwesomeIcon icon={faBuilding}/>
          <Typography component="span">
            {defaultValues?.name
                ? translate('modalInstitution.editInstitution')
                : translate('modalInstitution.newInstitution')}
          </Typography>
        </ModalHeader>

        <DialogContent style={{ minWidth: '650px', padding: '10px' }}>
          <Grid container direction="column">
            <FormCountryIdentifier
                columnSize={12}
                control={control}
                watch={watch}
                setValue={setValue}
                getValues={getValues}
                clearErrors={clearErrors}
                hasDefaultSiret={defaultValues?.siret}
                identifierFields={identifierFields}
                setIdentifierFields={setIdentifierFields}
            />

            {!UserHelper.hasAccessRight(DISTRIBUTOR_ROLES) && (
                <>
                  <FormControl>
                    <FormLine>
                      <Controller
                          control={control}
                          name="attestationNumber"
                          render={({ field, fieldState: { error } }) => (
                              <TextField
                                  {...field}
                                  autoComplete="off"
                                  inputProps={{ maxLength: 32 }}
                                  label={translate('common.attestationNumber')}
                                  error={!!error}
                                  helperText={error?.message}
                              />
                          )}
                      />
                    </FormLine>
                  </FormControl>
                  <FormControl>
                    <FormLine>
                      <Controller
                          control={control}
                          name="fileNumberPrefix"
                          render={({ field, fieldState: { error } }) => (
                              <TextField
                                  {...field}
                                  autoComplete="off"
                                  inputProps={{ maxLength: 32 }}
                                  label={translate('modalInstitution.customFileNumber')}
                                  error={!!error}
                                  helperText={error?.message}
                              />
                          )}
                      />
                    </FormLine>
                  </FormControl>
                </>
            )}
            <FormAddress
                control={control}
                watch={watch}
                setValue={setValue}
                getValues={getValues}
                isRequired
                withCountry={false}
            />

            {!UserHelper.hasAccessRight(DISTRIBUTOR_ROLES) && (
                <>
                  <SelectLabel>{translate('common.cerfaAutomaticDelivery')}</SelectLabel>

                  <FormControl>
                    <FormLine>
                      <Controller
                          control={control}
                          name="automaticCerfaDelivery"
                          render={({ field: { value, onChange, ...field } }) => (
                              <FormControlLabel
                                  control={
                                    <Checkbox
                                        checked={value || false}
                                        onChange={(e) => onChange(e.target.checked)}
                                        {...field}
                                    />
                                  }
                                  label={translate('common.activated')}
                              />
                          )}
                      />
                    </FormLine>
                  </FormControl>
                </>
            )}
          </Grid>
        </DialogContent>

        <CustomActionButtons
            isLoading={isLoading}
            isValidated={defaultValues?.validated}
            onClose={onClose}
        />
      </form>
  );
};

CreateInstitutionModal.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  closeOnSubmit: PropTypes.bool,
  defaultValues: PropTypes.shape({})
};

CreateInstitutionModal.defaultProps = {
  closeOnSubmit: true,
  defaultValues: {}
};